import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CdkDrag, CdkDragHandle, CdkDragPreview, CdkDropList } from '@angular/cdk/drag-drop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import {
    BulkGraftPoint,
    BulkPartNode,
    BulkSaveMove,
    BulkSaveReorder,
    BulkSaveUpdate,
    OrgPart,
    toTaskPresentation,
} from '@newgenus/common';
import { MaterialModule } from '../../../material.module';
import { OrgBoardService } from '../../services/org-board-service';

import { BuilderConfig, BuilderSavePayload, BuilderTreeNode, builderNodeToBulkPartNode } from './builder-models';
import { boardRootPartKey, mayJoinDetachedGroup } from '../../detached-group-rules';

/** Icons for part types — single source of truth used by both dialog wrappers. */
const PART_TYPE_ICONS: Record<string, string> = {
    executive: 'stars',
    division: 'domain',
    department: 'business',
    section: 'workspaces',
    unit: 'group_work',
    post: 'person',
};

/**
 * Org-Part Tree Builder — the shared UI for both the Edit-Position dialog and
 * the Bulk-Create-Position dialog. Both dialogs supply a `BuilderConfig` plus
 * an `initialTree: BuilderTreeNode[]` and listen for `(closeRequested)`.
 *
 * The component owns: render of the flat-tree builder UI, drag-drop, dirty
 * tracking, save-payload assembly, the call into `config.saveCommand(...)`,
 * task-monitor gating, and continue-editing.
 *
 * The wrapper owns: building the initial tree from its domain inputs,
 * building the breadcrumb, the pre-save warning hook (edit only), the
 * `obService.bulkSave(orgKey, ...)` closure, and the refresh-tree closure
 * for Continue Editing.
 */
@Component({
    selector: 'lib-org-part-tree-builder',
    imports: [MaterialModule, FormsModule, ReactiveFormsModule, CdkDropList, CdkDrag, CdkDragHandle, CdkDragPreview],
    templateUrl: './org-part-tree-builder.component.html',
    styleUrls: ['./org-part-tree-builder.component.scss'],
})
export class OrgPartTreeBuilderComponent implements OnChanges, OnDestroy {
    // ─────────────────────────────────────────────────────────────
    // Inputs / Outputs
    // ─────────────────────────────────────────────────────────────

    /** Per-launch configuration (mode, anchor, hooks, labels, organization). */
    @Input({ required: true }) config!: BuilderConfig;

    /** Initial tree to render. The component clones it on receipt; subsequent
     *  edits live entirely inside the component. */
    @Input({ required: true }) initialTree: BuilderTreeNode[] = [];

    /** Available part types (used as fallback for the type dropdown). */
    @Input() partTypes: string[] = ['executive', 'division', 'department', 'section', 'unit', 'post'];

    /** Render the developer-info footer when true (also gated by config.showDeveloperInfo). */
    @Input() isDeveloper = false;

    /** Emitted when the user closes the dialog. `saved` reflects whether at
     *  least one save round-trip completed in this session. */
    @Output() closeRequested = new EventEmitter<{ saved: boolean }>();

    private obService = inject(OrgBoardService);
    private hostEl = inject(ElementRef) as ElementRef<HTMLElement>;
    private cdr = inject(ChangeDetectorRef);

    // ─────────────────────────────────────────────────────────────
    // State
    // ─────────────────────────────────────────────────────────────

    /** Working tree. Multi-root; for edit mode the array has length 1. */
    tree: BuilderTreeNode[] = [];

    /** Per-row field visibility toggles. */
    showField = {
        typePill: true,
        name: true,
        subtitle: true,
        type: true,
        headerRole: true,
        vfp: false,
        colour: false,
    };

    /** Save phase tracking. */
    savePhase: 'idle' | 'writing' | 'processing' | 'done' | 'error' = 'idle';
    isSaving = false;
    saveProgress = 0;
    totalChanges = 0;
    taskStatuses: {
        key: string;
        name: string;
        action: string;
        icon: string;
        status: string;
        statusIcon: string;
        dedupKey?: string;
    }[] = [];
    tasksCompleted = 0;
    totalTasks = 0;
    private taskMonitorSub?: Subscription;
    private finishTimeout: ReturnType<typeof setTimeout> | null = null;
    private saveStartTime = 0;
    private expectedNewHatCount = 0;
    private reorderOnlySave = false;
    /** True once at least one save round-trip succeeded in this session. */
    private savedAtLeastOnce = false;

    /** Delete confirmation tracking. */
    pendingDeleteNode: BuilderTreeNode | null = null;

    /** Tracked deleted existing partKeys (Set so re-deletes are idempotent). */
    deletedPartKeys = new Set<string>();

    /** Whether developer section is expanded (edit-only). */
    devExpanded = false;

    /** Temp ID counter for new nodes. */
    private tempIdCounter = 0;

    /** Snapshot of root-level node order for orderChanged detection. */
    private initialSiblingOrder: string[] = [];

    /** True when the root-level sibling order has been modified vs baseline. */
    orderChanged = false;

    /** Snapshot of the original orgParts at the time `initialTree` was supplied,
     *  used by `collectDeletedKeys` for the edit-dialog's compare-vs-original
     *  delete tracking. Lazily extracted from `initialTree` via _originalParentPartKey
     *  walking — stored explicitly here so refreshes reset the baseline cleanly. */
    private originalKeysFromInitialTree = new Set<string>();

    /** AutoSave debounce timer (only used when `config.autoSave` is true). */
    private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
    /** Suppress auto-save while we're adopting a fresh tree (load / undo / redo). */
    private suppressAutoSave = false;

    // ── Undo / redo history (dialog mode only) ──────────────────────────
    // In autoSave (wizard) mode the host owns undo/redo, so the component's
    // history is inert there. Each entry is a deep snapshot of the working
    // tree plus the delete/order state that lives outside it.
    private history: { tree: BuilderTreeNode[]; deleted: string[]; orderChanged: boolean; baseline: string[] }[] = [];
    private historyIndex = -1;
    /** Debounce timer that coalesces a burst of field edits into one step. */
    private historyTimer: ReturnType<typeof setTimeout> | null = null;
    private static readonly HISTORY_LIMIT = 100;

    // ─────────────────────────────────────────────────────────────
    // Lifecycle
    // ─────────────────────────────────────────────────────────────

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initialTree'] && this.initialTree) {
            this.adoptTree(this.initialTree);
            if (this.config?.seedBoardSeat && !this.boardSeatSeeded && this.tree[0]) {
                this.boardSeatSeeded = true;
                this.addBoardSeat(this.tree[0], this.config.seedBoardSeat.connectsTo || '');
            }
        }
    }

    /** One-shot guard: the seed runs on the first tree adoption only. */
    private boardSeatSeeded = false;

    /**
     * Add a new post under `root` already placed in the Board of Directors box
     * (the box's "Add board seat" action). Undoable like any other add.
     */
    addBoardSeat(root: BuilderTreeNode, connectsTo = ''): void {
        if (this.isLeafType(root.partType) || this.isAtLimit) return;
        const seat = this.createNewNode(
            root.partKey,
            'post',
            root.colour,
            (root.depth ?? root.level) + 1,
            root.level + 1
        );
        seat.name = 'Board Seat';
        seat.detachedGroup = 'board';
        seat.detachedGroupConnectsTo = connectsTo;
        seat.excludeFromHfa = true;
        root.children.push(seat);
        root.collapsed = false;
        this.rebuildFlatRows();
        this.commitHistory();
        this.scrollNodeIntoView(seat.partKey);
    }

    /** A row that renders in the Board of Directors box — styled amber like the box. */
    isBoardSeat(row: { node: BuilderTreeNode; parentNode: BuilderTreeNode | null }): boolean {
        return !!row.node.detachedGroup && this.canPlaceInDetachedGroup(row);
    }

    ngOnDestroy(): void {
        this.taskMonitorSub?.unsubscribe();
        if (this.finishTimeout) clearTimeout(this.finishTimeout);
        if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
        if (this.historyTimer) clearTimeout(this.historyTimer);
    }

    /** Replace the working tree with a fresh copy of the supplied tree. */
    private adoptTree(nextTree: BuilderTreeNode[]): void {
        // Adopting a tree from outside (load / undo / redo) must NOT trigger
        // an auto-save round-trip — that would push the same state right back
        // onto the wizard's history stack and break undo/redo.
        this.suppressAutoSave = true;
        if (this.autoSaveTimer) {
            clearTimeout(this.autoSaveTimer);
            this.autoSaveTimer = null;
        }
        this.tree = nextTree;
        // Snapshot original keys for delete-tracking (any node currently in the tree
        // that isn't new). Wrappers re-issue `initialTree` on continue-editing, which
        // resets this baseline.
        this.originalKeysFromInitialTree = new Set<string>();
        const collect = (n: BuilderTreeNode) => {
            if (!n.isNew) this.originalKeysFromInitialTree.add(n.partKey);
            n.children.forEach(collect);
        };
        nextTree.forEach(collect);
        this.deletedPartKeys.clear();
        this.initialSiblingOrder = nextTree.map((n) => n.partKey);
        this.orderChanged = false;
        this.rebuildFlatRows();
        this.suppressAutoSave = false;
        this.initHistory();
    }

    // ─────────────────────────────────────────────────────────────
    // AutoSave (wizard mode) — debounced flush of every mutation back
    // to the wrapper, which records it in the wizard's undo/redo stack.
    // ─────────────────────────────────────────────────────────────

    /** Schedule a debounced auto-save flush (no-op outside autoSave mode). */
    private scheduleAutoSave(): void {
        if (!this.config?.autoSave) return;
        if (this.suppressAutoSave) return;
        if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
        this.autoSaveTimer = setTimeout(() => {
            this.autoSaveTimer = null;
            this.flushAutoSave();
        }, 500);
    }

    /** Push the current tree back to the wrapper. AutoSave skips the
     *  full payload-assembly path because wizard saves only consume the
     *  flattened tree — saving us from also having to keep dirty/move
     *  bookkeeping in sync. */
    private flushAutoSave(): void {
        if (!this.config?.autoSave) return;
        const emptyPayload: BuilderSavePayload = {
            updates: [],
            moves: [],
            add: null,
            reorders: [],
            deletes: [],
            expectedNewHatCount: 0,
            reorderOnlySave: false,
        };
        // Fire-and-forget — wrapper's saveCommand returns void/Promise<void>;
        // any error surfaces in console (autosave is best-effort, the user can
        // re-edit if it doesn't take). We don't toggle savePhase so the UI
        // stays clean.
        Promise.resolve(this.config.saveCommand(emptyPayload, this.tree)).catch((err) => {
            console.error('[OrgPartTreeBuilder] AutoSave failed:', err);
        });
    }

    // ─────────────────────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────────────────────

    getPartTypeIcon(type: string): string {
        return PART_TYPE_ICONS[type] || 'folder';
    }

    /** Return allowed child types for the given parent type / level. */
    getAllowedChildTypes(parentType: string, level: number): string[] {
        return this.config.getAllowedChildTypes(parentType, level);
    }

    isLeafType(partType: string): boolean {
        return this.getAllowedChildTypes(partType, 0).length === 0;
    }

    /** Memoised per flat-row rebuild / field edit — template calls this every CD pass. */
    private typeOptionsCache = new Map<string, string[]>();

    /**
     * The types a row may legally BE, given where it sits (ISS-009). Offering
     * an illegal type let the backend's validatePartType silently revert the
     * choice, which read as "my change didn't apply". Rules:
     *  - candidates = the parent's allowed child types;
     *  - a candidate must be able to hold the node's EXISTING children;
     *  - the current type always stays selectable (legacy data must not blank
     *    the select);
     *  - a root row (no parent) is locked to its current type.
     */
    typeOptionsFor(row: { node: BuilderTreeNode; parentNode: BuilderTreeNode | null }): string[] {
        const key = row.node.partKey;
        const cached = this.typeOptionsCache.get(key);
        if (cached) return cached;

        const node = row.node;
        const current = node.partType;
        let options: string[];
        if (!row.parentNode) {
            options = [current];
        } else {
            let candidates = this.getAllowedChildTypes(row.parentNode.partType, node.level ?? 0) || [];
            const children = node.children || [];
            if (children.length) {
                candidates = candidates.filter((t) => {
                    const allowed = this.getAllowedChildTypes(t, 0) || [];
                    return children.every((c) => allowed.includes(c.partType));
                });
            }
            options = candidates.includes(current) ? [...candidates] : [...candidates, current];
        }
        this.typeOptionsCache.set(key, options);
        return options;
    }

    hasChildren(node: BuilderTreeNode): boolean {
        return node.children?.length > 0;
    }

    trackByKey = (_i: number, node: BuilderTreeNode) => node.partKey;
    trackFlatRow = (_i: number, row: { node: BuilderTreeNode }) => row.node.partKey;

    /** True when the maximum number of new positions has been reached. */
    get isAtLimit(): boolean {
        return this.config.maxPositions != null && this.newCount >= this.config.maxPositions;
    }

    /** Whether `node` is the protected root in single-root edit mode. */
    isProtectedRoot(node: BuilderTreeNode): boolean {
        return !!this.config.protectedRootPartKey && node.partKey === this.config.protectedRootPartKey;
    }

    /** Memoised division list for the detached-group "connects to" picker. */
    private _divisionOptions: { partKey: string; name: string }[] | null = null;

    /** The organization's divisions — connector targets for a detached box. */
    get divisionOptions(): { partKey: string; name: string }[] {
        if (this._divisionOptions) return this._divisionOptions;
        const parts: any[] = this.config?.organization?.orgParts || [];
        this._divisionOptions = parts
            .filter((p) => p?.partType === 'division')
            .map((p) => ({ partKey: p.partKey, name: p.name || p.partKey }));
        return this._divisionOptions;
    }

    // ── Linked posts (org-board/linked-posts.ts) ──────────────────────────────
    // A linked post REPRESENTS another post (Department 19's "Office of the CFO" shows whoever
    // holds Chief Financial Officer). It comes from that post, so it cannot be changed where it
    // sits (Paul, 2026-10-21): its row is read-only here — no edit, drag, add or delete — and the
    // link itself is set only by the migration (19-link-posts). linkedPartKey still rides through
    // builder-models so a bulk save never drops it. Target names memoised per orgParts array.
    private _linkFor: unknown = undefined;
    private _partNames = new Map<string, string>();

    private refreshLinkCache(): void {
        const parts = this.config?.organization?.orgParts;
        if (parts === this._linkFor) return;
        this._linkFor = parts;
        this._partNames = new Map(
            ((parts || []) as any[])
                .filter((p) => !!p && !!p.partKey)
                .map((p) => [p.partKey as string, String(p.name || '').trim()])
        );
    }

    /** True when `node` is a post linked to (representing) another post — its row is read-only. */
    isLinked(node: BuilderTreeNode): boolean {
        return node?.partType === 'post' && !!node.linkedPartKey;
    }

    /** Display name of the post `node` links to ('' when unlinked). */
    linkedTargetName(node: BuilderTreeNode): string {
        if (!this.isLinked(node)) return '';
        this.refreshLinkCache();
        return this._partNames.get(node.linkedPartKey as string) || 'another post';
    }

    /** The board root (the CEO's container) — the only home for Board of Directors posts.
     *  Memoised per orgParts array: the template asks per row on every CD pass. */
    private get detachedGroupRootKey(): string | null {
        const parts = this.config?.organization?.orgParts;
        if (parts !== this._rootKeyFor) {
            this._rootKeyFor = parts;
            this._rootKey = boardRootPartKey(parts);
        }
        return this._rootKey;
    }
    private _rootKeyFor: unknown = undefined;
    private _rootKey: string | null = null;

    /** Parent key of `node`: its live tree parent, else its stored parent (a tree root). */
    private parentKeyOf(node: BuilderTreeNode, parentNode: BuilderTreeNode | null): string {
        return parentNode ? parentNode.partKey : node.parentPartKey || '';
    }

    /**
     * Whether this row may be placed in the Board of Directors box: a post sitting
     * directly in the board root, next to the CEO (detached-group-rules.ts). The
     * board draws the box nowhere else, so every other row gets no Placement control.
     */
    canPlaceInDetachedGroup(row: { node: BuilderTreeNode; parentNode: BuilderTreeNode | null }): boolean {
        if (this.isProtectedRoot(row.node)) return false;
        return mayJoinDetachedGroup(
            row.node.partType,
            this.parentKeyOf(row.node, row.parentNode),
            this.detachedGroupRootKey
        );
    }

    /**
     * Save-time guard: a post that is not (or no longer, after a drag) in the board
     * root loses any Board of Directors placement, so it cannot be written anywhere
     * the board would never draw it. Existing parts that carried one get the three
     * fields nulled by `collectDirtyParts`; new posts simply omit them.
     */
    private dropIneligibleDetachedPlacements(): void {
        const rootKey = this.detachedGroupRootKey;
        const walk = (node: BuilderTreeNode, parent: BuilderTreeNode | null) => {
            if (node.detachedGroup && !mayJoinDetachedGroup(node.partType, this.parentKeyOf(node, parent), rootKey)) {
                node.detachedGroup = '';
                node.detachedGroupConnectsTo = '';
                node.excludeFromHfa = node._original.detachedGroup ? false : !!node._original.excludeFromHfa;
                this.markDirty(node);
            }
            node.children.forEach((c) => walk(c, node));
        };
        this.tree.forEach((n) => walk(n, null));
    }

    /**
     * A post's detached-group placement changed (e.g. moved into / out of the
     * Board of Directors box). Board seats govern rather than run the org, so
     * entering a group defaults the post OUT of Held-From-Above; leaving the
     * group clears the connector + the HFA exclusion.
     */
    onDetachedGroupChange(node: BuilderTreeNode): void {
        if (node.detachedGroup) {
            if (!node._original.detachedGroup) node.excludeFromHfa = true;
        } else {
            node.detachedGroupConnectsTo = '';
            node.excludeFromHfa = false;
        }
        this.markDirty(node);
    }

    /** Whether the subtitle field should render for `node`. */
    showSubtitleField(node: BuilderTreeNode): boolean {
        if (!this.showField.subtitle) return false;
        if (this.config.hideSubtitleForNewNodes && node.isNew) return false;
        return true;
    }

    // ─────────────────────────────────────────────────────────────
    // Dirty tracking
    // ─────────────────────────────────────────────────────────────

    markDirty(node: BuilderTreeNode): void {
        // A type change alters what this node AND its children may become.
        this.typeOptionsCache.clear();
        const o = node._original;
        node.dirty =
            node.name !== o.name ||
            (node.subTitle || '') !== (o.subTitle || '') ||
            node.partType !== o.partType ||
            node.colour.background !== o.colour.background ||
            node.colour.color !== o.colour.color ||
            node.colour.linkColor !== o.colour.linkColor ||
            (node.vfp || '') !== (o.vfp || '') ||
            (node.headerRole || '') !== (o.headerRole || '') ||
            (node.detachedGroup || '') !== (o.detachedGroup || '') ||
            (node.detachedGroupConnectsTo || '') !== (o.detachedGroupConnectsTo || '') ||
            (node.excludeFromHfa === true) !== (o.excludeFromHfa === true) ||
            (node.linkedPartKey || '') !== (o.linkedPartKey || '');
        this.scheduleAutoSave();
        this.scheduleHistoryCommit();
    }

    // ─────────────────────────────────────────────────────────────
    // Counters / change collection
    // ─────────────────────────────────────────────────────────────

    private walkAll(): BuilderTreeNode[] {
        const out: BuilderTreeNode[] = [];
        const visit = (n: BuilderTreeNode) => {
            out.push(n);
            n.children.forEach(visit);
        };
        this.tree.forEach(visit);
        return out;
    }

    get dirtyCount(): number {
        return this.walkAll().filter((n) => n.dirty && !n.isNew).length;
    }
    get newCount(): number {
        return this.walkAll().filter((n) => n.isNew).length;
    }
    get movedCount(): number {
        return this.walkAll().filter((n) => n.moved && !n.isNew).length;
    }
    get deletedCount(): number {
        return this.deletedPartKeys.size;
    }
    get totalDescendants(): number {
        // For edit-mode (single root), exclude the root itself. For bulk-mode,
        // the count is "total nodes in the builder" which the bulk dialog
        // typically displays as "X new" via newCount instead.
        const total = this.walkAll().length;
        return this.config.mode === 'edit' && this.tree.length === 1 ? total - 1 : total;
    }

    get hasChanges(): boolean {
        return (
            this.dirtyCount > 0 ||
            this.newCount > 0 ||
            this.movedCount > 0 ||
            this.deletedCount > 0 ||
            this.orderChanged
        );
    }

    /** Tooltip describing every active status flag for a row. Multiple flags
     *  combine (e.g. "New + Moved" or "Modified + Moved"). */
    getStatusTooltip(node: BuilderTreeNode): string {
        const parts: string[] = [];
        if (node.isNew) parts.push('New');
        if (node.moved && !node.isNew) parts.push('Moved');
        if (node.dirty && !node.isNew) parts.push('Modified');
        return parts.join(' + ');
    }

    /** Pending changes summary for the right-hand panel. */
    get changesSummary(): { key: string; name: string; action: string; icon: string; label: string }[] {
        const changes: { key: string; name: string; action: string; icon: string; label: string }[] = [];

        // Modified existing nodes
        for (const n of this.walkAll().filter((x) => x.dirty && !x.isNew)) {
            changes.push({
                key: `mod-${n.partKey}`,
                name: n.name || 'Unnamed',
                action: 'modified',
                icon: 'edit',
                label: 'modified',
            });
        }

        // New nodes
        for (const n of this.walkAll().filter((x) => x.isNew)) {
            changes.push({
                key: `new-${n.partKey}`,
                name: n.name || 'Unnamed',
                action: n.moved ? 'add-moved' : 'add',
                icon: 'add_circle',
                label: n.moved ? 'new + moved' : 'new',
            });
        }

        // Moved existing nodes (skip nodes already shown as new+moved above)
        for (const n of this.walkAll().filter((x) => x.moved && !x.isNew)) {
            changes.push({
                key: `move-${n.partKey}`,
                name: n.name || 'Unnamed',
                action: 'moved',
                icon: 'swap_vert',
                label: 'reordered',
            });
        }

        // Deleted nodes
        for (const partKey of this.deletedPartKeys) {
            const orig = this.findOriginalName(partKey);
            changes.push({
                key: `del-${partKey}`,
                name: orig || partKey,
                action: 'remove',
                icon: 'remove_circle',
                label: 'deleted',
            });
        }

        return changes;
    }

    /** Lookup name from the org for a deleted partKey. */
    private findOriginalName(partKey: string): string | null {
        const orgParts = this.config.organization?.orgParts || [];
        return orgParts.find((p) => p.partKey === partKey)?.name || null;
    }

    get totalPendingChanges(): number {
        return this.changesSummary.length;
    }

    // ─────────────────────────────────────────────────────────────
    // Drag & Drop — Flat-row tree with cursor-X re-parenting
    // ─────────────────────────────────────────────────────────────
    //
    // We render a single flat list of all visible rows. During drag, cursor X
    // chooses target depth (and therefore parent), cursor Y chooses insertion
    // index. CDK's own currentIndex / previousIndex are IGNORED — we apply our
    // cached intent on dragEnded.

    flatRows: { node: BuilderTreeNode; depth: number; parentNode: BuilderTreeNode | null; indexInParent: number }[] =
        [];

    /** Pixel indent per depth level. Must match CSS. */
    static readonly INDENT_PX = 28;

    draggedNode: BuilderTreeNode | null = null;

    /** The branch we auto-collapsed at drag-start so the whole subtree lifts as
     *  one unit (rather than leaving the children rendered in place, orphaned,
     *  while the parent row is removed into the floating ghost). Restored to
     *  expanded on drag-end. Null when the dragged node had no children or was
     *  already collapsed by the user. */
    private dragAutoCollapsedNode: BuilderTreeNode | null = null;

    dropIntent: {
        afterIndex: number;
        depth: number;
        targetParent: BuilderTreeNode | null;
        indexInParent: number;
        indicatorTop: number;
        minDepth: number;
        maxDepth: number;
        zoneTop: number;
        zoneHeight: number;
        valid: boolean;
        invalidReason?: string;
    } | null = null;

    rebuildFlatRows(): void {
        this.typeOptionsCache.clear();
        const rows: typeof this.flatRows = [];
        const walk = (node: BuilderTreeNode, depth: number, parent: BuilderTreeNode | null, idx: number) => {
            rows.push({ node, depth, parentNode: parent, indexInParent: idx });
            if (node.collapsed) return;
            node.children.forEach((c, i) => walk(c, depth + 1, node, i));
        };
        this.tree.forEach((n, i) => walk(n, 0, null, i));
        this.flatRows = rows;
        // AutoSave (wizard mode only) — every structural mutation flows
        // through rebuildFlatRows. Collapse toggles set suppressAutoSave.
        this.scheduleAutoSave();
    }

    /** True when every non-protected node with children is collapsed. */
    get allCollapsed(): boolean {
        const walk = (n: BuilderTreeNode): boolean => {
            if (n.children.length === 0) return true;
            if (!this.isProtectedRoot(n) && !n.collapsed) return false;
            return n.children.every(walk);
        };
        return this.tree.every(walk);
    }

    toggleCollapse(node: BuilderTreeNode): void {
        node.collapsed = !node.collapsed;
        this.suppressAutoSave = true;
        this.rebuildFlatRows();
        this.suppressAutoSave = false;
    }

    toggleCollapseAll(): void {
        const collapse = !this.allCollapsed;
        const walk = (n: BuilderTreeNode) => {
            if (n.children.length > 0 && !this.isProtectedRoot(n)) n.collapsed = collapse;
            n.children.forEach(walk);
        };
        this.tree.forEach(walk);
        this.suppressAutoSave = true;
        this.rebuildFlatRows();
        this.suppressAutoSave = false;
    }

    /** Walk back from any node to its parent in the tree. */
    private findParentOf(node: BuilderTreeNode): BuilderTreeNode | null {
        const targetKey = node.partKey;
        const search = (cur: BuilderTreeNode): BuilderTreeNode | null => {
            for (const c of cur.children) {
                if (c.partKey === targetKey) return cur;
                const f = search(c);
                if (f) return f;
            }
            return null;
        };
        for (const r of this.tree) {
            const f = search(r);
            if (f) return f;
        }
        return null;
    }

    private isDescendant(candidate: BuilderTreeNode, node: BuilderTreeNode): boolean {
        if (candidate.partKey === node.partKey) return true;
        for (const c of node.children) {
            if (this.isDescendant(candidate, c)) return true;
        }
        return false;
    }

    onDragStarted(node: BuilderTreeNode): void {
        if (this.isProtectedRoot(node)) return;
        this.draggedNode = node;
        // Lift the whole branch as one unit. CDK removes only the dragged ROW
        // into the floating ghost — its children are separate rows that would
        // otherwise stay rendered in place, looking orphaned. Auto-collapse the
        // dragged node so `rebuildFlatRows` drops its descendants from the list;
        // we re-expand it on drag-end. Skipped when the node has no children or
        // the user had already collapsed it (we only restore what WE collapsed).
        if (this.hasChildren(node) && !node.collapsed) {
            node.collapsed = true;
            this.dragAutoCollapsedNode = node;
            this.suppressAutoSave = true;
            this.rebuildFlatRows();
            this.suppressAutoSave = false;
            // CDK drag fires outside Angular's zone, so the array change above
            // won't repaint mid-drag (the orphaned children would stay on screen).
            // Force change detection so the descendants actually leave the DOM.
            this.cdr.detectChanges();
        }
    }

    onDragEnded(): void {
        this.applyDropIntent();
        // Re-expand the branch we auto-collapsed at drag-start so its children
        // are visible again at the dropped location.
        if (this.dragAutoCollapsedNode) {
            this.dragAutoCollapsedNode.collapsed = false;
            this.dragAutoCollapsedNode = null;
        }
        this.draggedNode = null;
        this.dropIntent = null;
        this.stopAutoScroll();
        this.rebuildFlatRows();
        this.checkOrderChanged();
        this.commitHistory();
        // Drag-end also fires outside the zone — flush so the re-expanded branch
        // and any applied move repaint immediately.
        this.cdr.detectChanges();
    }

    /** Total number of descendants under a node — drives the drag-preview "+N"
     *  badge so the ghost makes clear the whole branch is moving. */
    descendantCount(node: BuilderTreeNode): number {
        let count = 0;
        const walk = (n: BuilderTreeNode) => {
            for (const c of n.children) {
                count++;
                walk(c);
            }
        };
        walk(node);
        return count;
    }

    // ─────────────────────────────────────────────────────────────
    // Undo / redo (dialog mode) — deep-snapshot history of the working
    // tree + the delete/order state that lives outside it. Structural ops
    // commit a discrete step; field edits coalesce a typing burst into one.
    // Inert in autoSave (wizard) mode, where the host owns undo/redo.
    // ─────────────────────────────────────────────────────────────

    private snapshotHistory() {
        return {
            tree: structuredClone(this.tree),
            deleted: Array.from(this.deletedPartKeys),
            orderChanged: this.orderChanged,
            baseline: [...this.initialSiblingOrder],
        };
    }

    private serializeHistory(s: {
        tree: BuilderTreeNode[];
        deleted: string[];
        orderChanged: boolean;
        baseline: string[];
    }): string {
        return JSON.stringify({ t: s.tree, d: s.deleted, o: s.orderChanged, b: s.baseline });
    }

    /** Seed the history with the freshly-adopted tree as the baseline step. */
    private initHistory(): void {
        if (this.historyTimer) {
            clearTimeout(this.historyTimer);
            this.historyTimer = null;
        }
        if (this.config?.autoSave) {
            this.history = [];
            this.historyIndex = -1;
            return;
        }
        this.history = [this.snapshotHistory()];
        this.historyIndex = 0;
    }

    /** Record the current state as a discrete undo step. No-op when nothing
     *  changed since the last step (so a no-op drag adds no entry). */
    commitHistory(): void {
        if (this.config?.autoSave) return;
        if (this.historyTimer) {
            clearTimeout(this.historyTimer);
            this.historyTimer = null;
        }
        const snap = this.snapshotHistory();
        const current = this.history[this.historyIndex];
        if (current && this.serializeHistory(current) === this.serializeHistory(snap)) return;
        // Drop any redo branch, append, advance.
        this.history.splice(this.historyIndex + 1);
        this.history.push(snap);
        if (this.history.length > OrgPartTreeBuilderComponent.HISTORY_LIMIT) this.history.shift();
        this.historyIndex = this.history.length - 1;
    }

    /** Debounced commit for continuous field edits (one step per typing burst). */
    private scheduleHistoryCommit(): void {
        if (this.config?.autoSave) return;
        if (this.historyTimer) clearTimeout(this.historyTimer);
        this.historyTimer = setTimeout(() => {
            this.historyTimer = null;
            this.commitHistory();
            this.cdr.detectChanges(); // refresh undo/redo enablement
        }, 500);
    }

    get canUndo(): boolean {
        return !this.config?.autoSave && this.historyIndex > 0;
    }
    get canRedo(): boolean {
        return !this.config?.autoSave && this.historyIndex < this.history.length - 1;
    }

    undo(): void {
        if (this.config?.autoSave) return;
        // A pending field-edit burst becomes its own step first, so one undo
        // reverts the burst rather than silently dropping it.
        if (this.historyTimer) this.commitHistory();
        if (this.historyIndex <= 0) return;
        this.historyIndex--;
        this.restoreHistory(this.history[this.historyIndex]);
    }

    redo(): void {
        if (this.config?.autoSave) return;
        if (this.historyIndex >= this.history.length - 1) return;
        this.historyIndex++;
        this.restoreHistory(this.history[this.historyIndex]);
    }

    private restoreHistory(s: {
        tree: BuilderTreeNode[];
        deleted: string[];
        orderChanged: boolean;
        baseline: string[];
    }): void {
        // Preserve the user's current expand/collapse (a view concern) across
        // undo/redo so reverting a rename doesn't also re-collapse branches.
        const collapse = new Map<string, boolean>();
        const capture = (n: BuilderTreeNode) => {
            collapse.set(n.partKey, n.collapsed);
            n.children.forEach(capture);
        };
        this.tree.forEach(capture);

        this.tree = structuredClone(s.tree);
        const apply = (n: BuilderTreeNode) => {
            if (collapse.has(n.partKey)) n.collapsed = collapse.get(n.partKey)!;
            n.children.forEach(apply);
        };
        this.tree.forEach(apply);

        this.deletedPartKeys = new Set(s.deleted);
        this.orderChanged = s.orderChanged;
        this.initialSiblingOrder = [...s.baseline];
        this.pendingDeleteNode = null;
        this.suppressAutoSave = true;
        this.rebuildFlatRows();
        this.suppressAutoSave = false;
        this.cdr.detectChanges();
    }

    /** Ctrl/⌘+Z = undo, Ctrl/⌘+Shift+Z or Ctrl+Y = redo (dialog mode only). */
    @HostListener('keydown', ['$event'])
    onHistoryKeydown(e: KeyboardEvent): void {
        if (this.config?.autoSave) return;
        if (!(e.ctrlKey || e.metaKey)) return;
        const key = e.key.toLowerCase();
        if (key === 'z' && !e.shiftKey) {
            e.preventDefault();
            this.undo();
        } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
            e.preventDefault();
            this.redo();
        }
    }

    /** Legacy CDK drop handler — kept as a no-op safety net. */
    onFlatDrop(): void {
        /* see onDragEnded */
    }

    onDragMoved(event: { pointerPosition: { x: number; y: number } }): void {
        if (!this.draggedNode) return;
        const container = this.hostEl.nativeElement.querySelector<HTMLElement>('.flat-tree-list');
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const cursorX = event.pointerPosition.x - rect.left + container.scrollLeft;
        const cursorY = event.pointerPosition.y;
        this.dropIntent = this.computeDropIntent(cursorX, cursorY, container);
        this.updateAutoScroll(cursorY);
    }

    // ── Edge auto-scroll while dragging ──
    private autoScrollSpeed = 0;
    private autoScrollRaf = 0;
    private static readonly AUTOSCROLL_ZONE = 60;
    private static readonly AUTOSCROLL_MAX_SPEED = 18;

    private updateAutoScroll(cursorY: number): void {
        const scroller = this.hostEl.nativeElement.querySelector<HTMLElement>('.builder-tree');
        if (!scroller) {
            this.autoScrollSpeed = 0;
            return;
        }
        const r = scroller.getBoundingClientRect();
        const zone = OrgPartTreeBuilderComponent.AUTOSCROLL_ZONE;
        const max = OrgPartTreeBuilderComponent.AUTOSCROLL_MAX_SPEED;
        let speed = 0;
        const distFromTop = cursorY - r.top;
        const distFromBottom = r.bottom - cursorY;
        if (distFromTop < zone && distFromTop > -zone) {
            const ratio = Math.max(0, Math.min(1, (zone - distFromTop) / zone));
            speed = -Math.ceil(ratio * max);
        } else if (distFromBottom < zone && distFromBottom > -zone) {
            const ratio = Math.max(0, Math.min(1, (zone - distFromBottom) / zone));
            speed = Math.ceil(ratio * max);
        }
        this.autoScrollSpeed = speed;
        if (speed !== 0 && this.autoScrollRaf === 0) {
            this.autoScrollTick(scroller);
        }
    }

    private autoScrollTick(scroller: HTMLElement): void {
        this.autoScrollRaf = requestAnimationFrame(() => {
            if (!this.draggedNode || this.autoScrollSpeed === 0) {
                this.autoScrollRaf = 0;
                return;
            }
            const before = scroller.scrollTop;
            scroller.scrollTop = before + this.autoScrollSpeed;
            if (scroller.scrollTop === before) {
                this.autoScrollRaf = 0;
                return;
            }
            this.autoScrollTick(scroller);
        });
    }

    private stopAutoScroll(): void {
        this.autoScrollSpeed = 0;
        if (this.autoScrollRaf !== 0) {
            cancelAnimationFrame(this.autoScrollRaf);
            this.autoScrollRaf = 0;
        }
    }

    /**
     * Compute drop intent from cursor position over the flat list.
     *
     *   1. Find row whose vertical mid-point is most recent above cursor → afterIndex.
     *   2. Compute depth bounds:
     *        maxDepth = afterRow.depth + 1  if afterRow can accept a child, else afterRow.depth.
     *        minDepth = config.minDepth     (1 for edit, 0 for bulk).
     *   3. Default depth = maxDepth; pull cursor LEFT past indent boundary to outdent.
     *   4. Resolve target parent at depth (desiredDepth - 1).
     *   5. Validate (leaf-type, descendant cycle, "existing under new").
     */
    private computeDropIntent(cursorX: number, cursorY: number, container: HTMLElement): typeof this.dropIntent {
        const rows = this.flatRows;
        if (rows.length === 0 || !this.draggedNode) return null;

        const rowEls = container.querySelectorAll<HTMLElement>('.flat-tree-row');
        const containerRect = container.getBoundingClientRect();
        let afterIndex = -1;
        let indicatorTop = 0;
        for (let i = 0; i < rowEls.length; i++) {
            const r = rowEls[i].getBoundingClientRect();
            if (r.height === 0) continue;
            const mid = r.top + r.height / 2;
            if (cursorY >= mid) {
                afterIndex = i;
                indicatorTop = r.bottom - containerRect.top + container.scrollTop;
            } else {
                break;
            }
        }

        const dragged = this.draggedNode;
        const minDepth = this.config.minDepth;

        // Cursor above all rows.
        if (afterIndex === -1) {
            const firstRect = rowEls[0]?.getBoundingClientRect();
            const top = firstRect ? firstRect.top - containerRect.top + container.scrollTop : 0;
            if (minDepth === 0) {
                // Bulk mode — root-level top insertion is valid.
                return {
                    afterIndex: -1,
                    depth: 0,
                    targetParent: null,
                    indexInParent: 0,
                    indicatorTop: top,
                    minDepth: 0,
                    maxDepth: 0,
                    zoneTop: top,
                    zoneHeight: firstRect?.height || 0,
                    valid: true,
                };
            }
            // Edit mode — cannot drop above the root.
            return {
                afterIndex: -1,
                depth: 0,
                targetParent: null,
                indexInParent: 0,
                indicatorTop: top,
                minDepth: 0,
                maxDepth: 0,
                zoneTop: top,
                zoneHeight: firstRect?.height || 0,
                valid: false,
                invalidReason: 'Cannot drop above root',
            };
        }

        const afterRowRect = rowEls[afterIndex].getBoundingClientRect();
        const zoneTop = afterRowRect.top - containerRect.top + container.scrollTop;
        const zoneHeight = afterRowRect.height;

        const afterRow = rows[afterIndex];

        const afterCanAcceptChild =
            !this.isLeafType(afterRow.node.partType) && !this.isDescendant(afterRow.node, dragged);
        const maxDepth = Math.max(minDepth, afterCanAcceptChild ? afterRow.depth + 1 : afterRow.depth);

        const indentCol = Math.floor(cursorX / OrgPartTreeBuilderComponent.INDENT_PX);
        let desiredDepth = maxDepth;
        if (indentCol < maxDepth) {
            // Pull-left to outdent. Clamp to minDepth.
            desiredDepth = Math.max(minDepth, indentCol < 0 ? minDepth : indentCol);
        }

        // Resolve target parent at depth (desiredDepth - 1). null = root level.
        let targetParent: BuilderTreeNode | null = null;
        if (desiredDepth > 0) {
            let cursorWalk: { node: BuilderTreeNode; depth: number } | null = {
                node: afterRow.node,
                depth: afterRow.depth,
            };
            while (cursorWalk && cursorWalk.depth > desiredDepth - 1) {
                const parent = this.findParentOf(cursorWalk.node);
                if (!parent) {
                    cursorWalk = null;
                    break;
                }
                cursorWalk = { node: parent, depth: cursorWalk.depth - 1 };
            }
            if (cursorWalk && cursorWalk.depth === desiredDepth - 1) {
                targetParent = cursorWalk.node;
            }
        }

        // Resolve insertion index in target.
        let indexInParent = 0;
        const targetSiblings: BuilderTreeNode[] = targetParent ? targetParent.children : this.tree;
        if (targetParent && afterRow.node.partKey === targetParent.partKey) {
            indexInParent = 0;
        } else {
            let candidate: BuilderTreeNode | null = afterRow.node;
            let candidateDepth = afterRow.depth;
            while (candidate && candidateDepth > desiredDepth) {
                candidate = this.findParentOf(candidate);
                candidateDepth--;
            }
            if (candidate && candidateDepth === desiredDepth) {
                const idx = targetSiblings.indexOf(candidate);
                indexInParent = idx >= 0 ? idx + 1 : targetSiblings.length;
            } else {
                indexInParent = targetSiblings.length;
            }
        }

        // Validation
        let valid = true;
        let invalidReason: string | undefined;
        if (desiredDepth > 0 && !targetParent) {
            valid = false;
            invalidReason = 'No valid parent at this depth';
        } else if (targetParent && this.isLeafType(targetParent.partType)) {
            valid = false;
            invalidReason = `${targetParent.partType} cannot have children`;
        } else if (targetParent && this.isDescendant(targetParent, dragged)) {
            valid = false;
            invalidReason = 'Cannot drop into self or descendant';
        } else if (targetParent && targetParent.isNew && !dragged.isNew) {
            valid = false;
            invalidReason = 'Cannot move existing under new (save first)';
        }

        return {
            afterIndex,
            depth: desiredDepth,
            targetParent,
            indexInParent,
            indicatorTop,
            minDepth,
            maxDepth,
            zoneTop,
            zoneHeight,
            valid,
            invalidReason,
        };
    }

    /** Helper for template — render columns from minDepth..maxDepth. */
    depthZoneRange(): number[] {
        if (!this.dropIntent) return [];
        const out: number[] = [];
        for (let d = this.dropIntent.minDepth; d <= this.dropIntent.maxDepth; d++) out.push(d);
        return out;
    }

    /**
     * 1-based depth shown in the UI (labels + colour palette). Decouples the
     * visual depth from the tree's absolute depth so both modes look identical
     * regardless of `minDepth`:
     *   edit  (minDepth=1) → displayDepth(1)=1, displayDepth(2)=2  (unchanged)
     *   bulk  (minDepth=0) → displayDepth(0)=1, displayDepth(1)=2  (matches edit)
     * The SCSS palette keys off `[data-depth='N']` (`N = displayDepth`), so the
     * first available zone is always indigo, the second always blue, etc.
     */
    displayDepth(absoluteDepth: number): number {
        const minDepth = this.dropIntent?.minDepth ?? 0;
        return absoluteDepth - minDepth + 1;
    }

    private applyDropIntent(): void {
        const intent = this.dropIntent;
        const node = this.draggedNode;
        if (!intent || !node || !intent.valid) return;

        const oldParent = this.findParentOf(node);
        const oldSiblings: BuilderTreeNode[] = oldParent ? oldParent.children : this.tree;
        const oldIndex = oldSiblings.indexOf(node);
        if (oldIndex < 0) return;

        const newSiblings: BuilderTreeNode[] = intent.targetParent ? intent.targetParent.children : this.tree;
        const sameContainer = oldSiblings === newSiblings;
        let newIndex = intent.indexInParent;
        if (sameContainer && oldIndex < newIndex) newIndex--;

        // No-op: dropped at same position. Bail without mutating or marking moved.
        if (sameContainer && newIndex === oldIndex) return;

        // Mutate
        oldSiblings.splice(oldIndex, 1);
        newSiblings.splice(newIndex, 0, node);

        // Update parentPartKey on the moved node.
        node.parentPartKey = intent.targetParent ? intent.targetParent.partKey : this.config.anchorPartKey || '';

        // Cascade level for the moved subtree.
        const newLevel = intent.targetParent ? intent.targetParent.level + 1 : 0;
        const levelDelta = newLevel - node.level;
        if (levelDelta !== 0) {
            const cascade = (n: BuilderTreeNode) => {
                n.level += levelDelta;
                if (typeof n.depth === 'number') n.depth += levelDelta;
                for (const c of n.children) cascade(c);
            };
            cascade(node);
        }

        // Mark moved (for existing nodes). Clear flag if the node returned to its
        // original parent + index within the same session.
        if (!node.isNew) {
            const backToOriginalParent = node.parentPartKey === (node._originalParentPartKey || '');
            const currentIndex = newSiblings.indexOf(node);
            const backToOriginalIndex = currentIndex === (node._originalIndex ?? -1);
            node.moved = !(backToOriginalParent && backToOriginalIndex);
        }

        if (intent.targetParent) intent.targetParent.collapsed = false;
    }

    // ─────────────────────────────────────────────────────────────
    // Order-baseline tracking (root-level dirty check)
    // ─────────────────────────────────────────────────────────────

    private updateOrderBaseline(): void {
        this.initialSiblingOrder = this.tree.map((n) => n.partKey);
    }

    private checkOrderChanged(): void {
        // Only existing-node order matters for `orderChanged` (new roots come
        // from add-buttons and don't constitute a "reorder").
        const currentExisting = this.tree.filter((n) => !n.isNew).map((n) => n.partKey);
        const baselineExisting = this.initialSiblingOrder.filter((k) => this.originalKeysFromInitialTree.has(k));
        this.orderChanged =
            currentExisting.length === baselineExisting.length &&
            currentExisting.length > 0 &&
            currentExisting.some((k, i) => k !== baselineExisting[i]);

        // Mark each existing root that moved from its baseline position.
        for (const node of this.tree) {
            if (node.isNew) continue;
            const initialIndex = baselineExisting.indexOf(node.partKey);
            const currentIndex = currentExisting.indexOf(node.partKey);
            if (initialIndex >= 0) {
                node.moved = initialIndex !== currentIndex || node.moved;
            }
        }
    }

    // ─────────────────────────────────────────────────────────────
    // Add / Delete
    // ─────────────────────────────────────────────────────────────

    /** Add a top-level (root) node. Bulk mode only — gated by config.showAddRootButton. */
    addRootNode(): void {
        if (this.isAtLimit) return;
        const inferredType = this.inferRootPartType();
        const node = this.createNewNode(this.config.anchorPartKey || '', inferredType, this.config.defaultColour, 0, 0);
        node.partType = inferredType;
        node._original.partType = inferredType;
        this.tree.push(node);
        this.updateOrderBaseline();
        this.rebuildFlatRows();
        this.commitHistory();
        this.scrollNodeIntoView(node.partKey);
    }

    /** Determine the default part-type for a new root-level node based on
     *  the anchor's type. Mirrors the bulk dialog's logic. */
    private inferRootPartType(): string {
        const anchorType =
            this.config.breadcrumb.length > 0 ? this.config.breadcrumb[this.config.breadcrumb.length - 1].partType : '';
        if (!anchorType) return 'post';
        if (this.config.insertionType === 'sibling') return anchorType;
        // child mode → first allowed child type at root level.
        const allowed = this.getAllowedChildTypes(anchorType, 0);
        return allowed[0] || 'post';
    }

    addChild(parent: BuilderTreeNode): void {
        if (this.isLeafType(parent.partType)) return;
        if (this.isAtLimit) return;
        const childType = this.suggestChildType(parent.partType, parent.level + 1);
        const newNode = this.createNewNode(
            parent.partKey,
            childType,
            parent.colour,
            (parent.depth ?? parent.level) + 1,
            parent.level + 1
        );
        parent.children.push(newNode);
        parent.collapsed = false;
        this.rebuildFlatRows();
        this.commitHistory();
        this.scrollNodeIntoView(newNode.partKey);
    }

    addSibling(node: BuilderTreeNode, siblings: BuilderTreeNode[]): void {
        if (this.isAtLimit) return;
        const idx = siblings.indexOf(node);
        const newNode = this.createNewNode(
            node.parentPartKey,
            node.partType,
            node.colour,
            node.depth ?? node.level,
            node.level
        );
        newNode.partType = node.partType;
        newNode._original.partType = node.partType;
        siblings.splice(idx + 1, 0, newNode);
        if (siblings === this.tree) this.updateOrderBaseline();
        this.rebuildFlatRows();
        this.commitHistory();
        this.scrollNodeIntoView(newNode.partKey);
    }

    private suggestChildType(parentType: string, level: number): string {
        const allowed = this.getAllowedChildTypes(parentType, level);
        return allowed[0] || 'post';
    }

    private createNewNode(
        parentPartKey: string,
        partType: string,
        colour: { background: string; color: string; linkColor: string },
        depth: number,
        level: number
    ): BuilderTreeNode {
        this.tempIdCounter++;
        const tempKey = `new-${Date.now()}-${this.tempIdCounter}`;
        return {
            partKey: tempKey,
            isNew: true,
            name: '',
            subTitle: '',
            partType,
            colour: { ...colour },
            vfp: '',
            headerRole: '',
            children: [],
            parentPartKey,
            depth,
            level,
            collapsed: false,
            dirty: false,
            moved: false,
            _original: {
                name: '',
                subTitle: '',
                partType,
                colour: { ...colour },
                vfp: '',
                headerRole: '',
            },
        };
    }

    private scrollNodeIntoView(partKey: string): void {
        setTimeout(() => {
            const root = this.hostEl.nativeElement;
            const el = root.querySelector<HTMLElement>(`[data-part-key="${partKey}"]`);
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = el.querySelector<HTMLInputElement>('.field-name input');
            if (input) {
                setTimeout(() => input.focus(), 150);
            }
        });
    }

    confirmDelete(node: BuilderTreeNode, parentChildren: BuilderTreeNode[], event: Event): void {
        event.stopPropagation();
        if (node.isNew) {
            const idx = parentChildren.indexOf(node);
            if (idx > -1) parentChildren.splice(idx, 1);
            if (parentChildren === this.tree) this.updateOrderBaseline();
            this.rebuildFlatRows();
            this.commitHistory();
            return;
        }
        this.pendingDeleteNode = node;
    }

    cancelDelete(): void {
        this.pendingDeleteNode = null;
    }

    executeDelete(node: BuilderTreeNode, parentChildren: BuilderTreeNode[]): void {
        // Track all existing partKeys in the deleted subtree.
        const collect = (n: BuilderTreeNode) => {
            if (!n.isNew) this.deletedPartKeys.add(n.partKey);
            n.children.forEach(collect);
        };
        collect(node);
        const idx = parentChildren.indexOf(node);
        if (idx > -1) parentChildren.splice(idx, 1);
        this.pendingDeleteNode = null;
        if (parentChildren === this.tree) this.updateOrderBaseline();
        this.rebuildFlatRows();
        this.commitHistory();
    }

    // ─────────────────────────────────────────────────────────────
    // Save
    // ─────────────────────────────────────────────────────────────

    async onSave(): Promise<void> {
        if (!this.hasChanges || this.isSaving) return;

        // Optional pre-save hook (edit dialog uses this for its post→container warning).
        if (this.config.preSave) {
            const ok = await this.config.preSave();
            if (!ok) return;
        }

        this.isSaving = true;
        this.savePhase = 'writing';

        // ── Collect change-sets ──
        this.dropIneligibleDetachedPlacements();
        const dirtyParts = this.collectDirtyParts();
        const moveOps = this.collectMoveOps();
        const deletedKeys = Array.from(this.deletedPartKeys);
        const addBundle = this.buildAddBundle();

        // Count `post`-type new parts — used by task monitor to wait for matching addHat tasks.
        this.expectedNewHatCount = 0;
        if (addBundle) {
            const countPosts = (parts: BulkPartNode[] | undefined) => {
                if (!parts) return;
                for (const p of parts) {
                    if (p.partType === 'post') this.expectedNewHatCount++;
                    if (Array.isArray(p.children)) countPosts(p.children);
                }
            };
            countPosts(addBundle.tree);
            for (const graft of addBundle.graftPoints || []) {
                countPosts(graft.children);
            }
        }

        // Reorders: only deep reorders NOT already covered by the add-bundle's siblingOrder
        // / graftPoint childOrder, and NOT covered by Phase-M moves.
        const coveredByAdd = new Set<string>();
        if (addBundle) {
            if (addBundle.siblingOrder) {
                // The add-bundle's siblingOrder governs the anchor's child order at root level.
                coveredByAdd.add(this.config.anchorPartKey || '');
            }
            for (const g of addBundle.graftPoints || []) coveredByAdd.add(g.existingPartKey);
        }
        const moveTargets = new Set<string>();
        for (const m of moveOps) {
            moveTargets.add(m.newParentPartKey);
            // Old parent of a moved node — derive from the node's _originalParentPartKey.
            const orig = this.walkAll().find((n) => n.partKey === m.partKey);
            if (orig?._originalParentPartKey) moveTargets.add(orig._originalParentPartKey);
        }
        const reorderOps = this.collectReorderOps().filter(
            (op) => !coveredByAdd.has(op.parentPartKey) && !moveTargets.has(op.parentPartKey)
        );

        // Validate at least one operation
        if (
            dirtyParts.length === 0 &&
            moveOps.length === 0 &&
            !addBundle &&
            reorderOps.length === 0 &&
            deletedKeys.length === 0
        ) {
            this.isSaving = false;
            this.savePhase = 'idle';
            return;
        }

        this.totalChanges =
            dirtyParts.length +
            moveOps.length +
            (addBundle ? 1 : 0) +
            reorderOps.length +
            (deletedKeys.length > 0 ? 1 : 0);
        this.saveProgress = 0;
        this.saveStartTime = Date.now();

        // reorderOnlySave: pure reorder, no other changes.
        const hasNewNodes = !!addBundle && (addBundle.tree.length > 0 || (addBundle.graftPoints?.length || 0) > 0);
        this.reorderOnlySave =
            !hasNewNodes && dirtyParts.length === 0 && moveOps.length === 0 && deletedKeys.length === 0;

        const updatesPayload: BulkSaveUpdate[] = dirtyParts
            .filter((p) => !!p.partKey)
            .map((p) => {
                const { partKey, ...fields } = p as any;
                return { partKey: partKey as string, fields };
            });

        const reordersPayload: BulkSaveReorder[] = reorderOps.map((op) => ({
            parentPartKey: op.parentPartKey,
            newChildOrder: op.childOrder,
        }));

        const payload: BuilderSavePayload = {
            updates: updatesPayload,
            moves: moveOps,
            add: addBundle,
            reorders: reordersPayload,
            deletes: deletedKeys,
            expectedNewHatCount: this.expectedNewHatCount,
            reorderOnlySave: this.reorderOnlySave,
        };

        try {
            await this.config.saveCommand(payload, this.tree);
            this.saveProgress = this.totalChanges;
            this.savedAtLeastOnce = true;
            if (this.config.skipTaskMonitor) {
                // Wizard adapter — `saveCommand` is a sync in-memory mutation,
                // there are no `securityTasks/` to wait on. Jump straight to done.
                this.savePhase = 'processing';
                this.finishSave();
            } else {
                this.savePhase = 'processing';
                this.monitorBackendTasks();
            }
        } catch (err) {
            console.error('[OrgPartTreeBuilder] Save failed:', err);
            this.savePhase = 'error';
            this.isSaving = false;
        }
    }

    // ─────────────────────────────────────────────────────────────
    // Save-payload assembly
    // ─────────────────────────────────────────────────────────────

    private collectDirtyParts(): Partial<OrgPart>[] {
        const out: Partial<OrgPart>[] = [];
        for (const n of this.walkAll()) {
            if (!n.dirty || n.isNew) continue;
            const patch: Partial<OrgPart> = {
                partKey: n.partKey,
                name: n.name,
                subTitle: n.subTitle,
                partType: n.partType,
                colour: { ...n.colour },
                vfp: n.vfp,
            };
            if (n.partType === 'post' && n.headerRole) {
                patch.headerRole = n.headerRole as 'head' | 'deputy' | 'assistant';
            } else if ((n._original.headerRole || '') && !(n.headerRole || '')) {
                (patch as any).headerRole = null;
            }
            // Detached-group placement (Board of Directors box). Setting the
            // group carries the connector + HFA exclusion; clearing it nulls
            // all three so the post returns to normal on-board placement.
            if (n.partType === 'post' && n.detachedGroup) {
                (patch as any).detachedGroup = n.detachedGroup;
                (patch as any).detachedGroupConnectsTo = n.detachedGroupConnectsTo || null;
                (patch as any).excludeFromHfa = n.excludeFromHfa === true;
            } else if (n._original.detachedGroup && !(n.detachedGroup || '')) {
                (patch as any).detachedGroup = null;
                (patch as any).detachedGroupConnectsTo = null;
                (patch as any).excludeFromHfa = null;
            }
            // Linked post: only written when it changed; clearing it saves null.
            if ((n.linkedPartKey || '') !== (n._original.linkedPartKey || '')) {
                (patch as any).linkedPartKey = n.partType === 'post' && n.linkedPartKey ? n.linkedPartKey : null;
            }
            out.push(patch);
        }
        return out;
    }

    private collectMoveOps(): BulkSaveMove[] {
        const out: BulkSaveMove[] = [];
        const visit = (siblings: BuilderTreeNode[]) => {
            for (let i = 0; i < siblings.length; i++) {
                const n = siblings[i];
                if (!n.isNew && (n._originalParentPartKey || '') !== (n.parentPartKey || '')) {
                    out.push({
                        partKey: n.partKey,
                        newParentPartKey: n.parentPartKey || '',
                        newIndex: i,
                    });
                }
                visit(n.children);
            }
        };
        visit(this.tree);
        return out;
    }

    private buildAddBundle(): BuilderSavePayload['add'] {
        // Walk the tree collecting:
        //   - root-level NEW children (under the anchor)        → add.tree
        //   - existing parents that have NEW children           → add.graftPoints
        // The root-level new children are the items at depth 0 (this.tree)
        // that are isNew. Existing roots' new children become graft points.

        const rootNewChildren = this.tree.filter((n) => n.isNew);
        const graftPoints: BulkGraftPoint[] = [];

        const collectGrafts = (siblings: BuilderTreeNode[]) => {
            for (const node of siblings) {
                if (!node.isNew) {
                    const newChildren = node.children.filter((c) => c.isNew);
                    if (newChildren.length > 0) {
                        graftPoints.push({
                            existingPartKey: node.partKey,
                            children: newChildren.map((c) => builderNodeToBulkPartNode(c)),
                            childOrder: node.children.map((c) => c.partKey),
                        });
                    }
                    // Recurse into existing children for deeper grafts.
                    collectGrafts(node.children.filter((c) => !c.isNew));
                }
            }
        };
        collectGrafts(this.tree);

        // Count total new across root tree + graft children (recursive).
        const countNew = (nodes: BuilderTreeNode[]): number => {
            let count = 0;
            for (const n of nodes) {
                if (n.isNew) count++;
                count += countNew(n.children);
            }
            return count;
        };
        const totalNew = countNew(this.tree);

        if (totalNew === 0 && !this.orderChanged) return null;

        const tree = rootNewChildren.map((n) => builderNodeToBulkPartNode(n));

        // siblingOrder: when the anchor's children are a mix of new + existing
        // (or order changed at root level), emit the full order so the backend
        // can interleave new tempIds with existing keys.
        let siblingOrder: string[] | undefined;
        const hasExistingRootChildren = this.tree.some((n) => !n.isNew);
        if ((rootNewChildren.length > 0 && hasExistingRootChildren) || this.orderChanged) {
            siblingOrder = this.tree.map((n) => n.partKey);
        }

        // If totalNew is 0 (pure reorder), still emit add bundle with empty tree
        // when orderChanged is true so siblingOrder is propagated. Otherwise the
        // backend reorders[] block handles it via the per-parent reorder ops.
        if (totalNew === 0) return null;

        return {
            anchorPartKey: this.config.anchorPartKey || '',
            insertionType: this.config.insertionType,
            tree,
            totalCount: totalNew,
            ...(siblingOrder ? { siblingOrder } : {}),
            ...(graftPoints.length > 0 ? { graftPoints } : {}),
        };
    }

    private collectReorderOps(): { parentPartKey: string; childOrder: string[] }[] {
        const out: { parentPartKey: string; childOrder: string[] }[] = [];
        const orgParts = this.config.organization?.orgParts || [];

        const visit = (node: BuilderTreeNode) => {
            // Only existing parents with ≥ 2 existing children can be reordered.
            if (!node.isNew) {
                const existingChildren = node.children.filter((c) => !c.isNew);
                if (existingChildren.length >= 2) {
                    const currentOrder = existingChildren.map((c) => c.partKey);
                    const originalOrder = orgParts
                        .filter((p) => p.parentPartKey === node.partKey)
                        .sort((a, b) => this.extractIndexFromPath(a.path) - this.extractIndexFromPath(b.path))
                        .map((p) => p.partKey);
                    const orderChanged = !currentOrder.every((k, i) => k === originalOrder[i]);
                    if (orderChanged) {
                        out.push({ parentPartKey: node.partKey, childOrder: currentOrder });
                    }
                }
            }
            node.children.forEach(visit);
        };
        this.tree.forEach(visit);
        return out;
    }

    private extractIndexFromPath(path: string): number {
        if (!path) return 0;
        const parts = path.split('.');
        for (let i = parts.length - 1; i >= 0; i--) {
            if (parts[i] === 'data' && i > 0 && parts[i - 1].match(/^\d+$/)) {
                return parseInt(parts[i - 1], 10);
            }
        }
        return 0;
    }

    // ─────────────────────────────────────────────────────────────
    // Backend task monitoring
    // ─────────────────────────────────────────────────────────────

    private monitorBackendTasks(): void {
        const orgParts = this.config.organization?.orgParts || [];
        this.taskStatuses = [];
        this.tasksCompleted = 0;
        this.totalTasks = 0;

        const timeoutHandle = setTimeout(() => {
            console.warn('[OrgPartTreeBuilder] Task monitor timed out after 60s');
            this.finishSave();
        }, 60000);

        this.taskMonitorSub = this.obService.monitorSecurityTasks().subscribe((tasks) => {
            const recentTasks = tasks.filter((t) => {
                const created = t.createdTimeStamp?.toMillis?.() || (t.createdTimeStamp as any)?.seconds * 1000 || 0;
                return created > this.saveStartTime - 2000;
            });

            let completed = 0;
            let total = 0;
            const statuses: typeof this.taskStatuses = [];

            for (const task of recentTasks) {
                const isTerminal =
                    task.status === 'completed' ||
                    task.status === 'fulfilled' ||
                    task.status === 'error' ||
                    task.status === 'Error' ||
                    task.status === 'failed';

                const presentation = toTaskPresentation(task, {
                    orgParts,
                    orgHats: (this.config.organization as any)?.orgHats,
                    reorderOnly: this.reorderOnlySave,
                });

                if (statuses.some((s) => s.dedupKey === presentation.dedupKey)) continue;

                statuses.push({
                    key: task.key,
                    name: presentation.name,
                    action: presentation.action,
                    icon: presentation.icon,
                    status: presentation.statusLabel,
                    statusIcon: presentation.statusIcon,
                    dedupKey: presentation.dedupKey,
                });
                total++;
                if (isTerminal) completed++;
            }

            this.taskStatuses = statuses;
            this.totalTasks = total;
            this.tasksCompleted = completed;

            const seenAddHatCount = statuses.filter((s) => s.dedupKey?.startsWith('addHat:')).length;
            const stillExpectingHats = this.expectedNewHatCount > 0 && seenAddHatCount < this.expectedNewHatCount;

            if (total > 0 && completed === total && !stillExpectingHats) {
                if (!this.finishTimeout) {
                    clearTimeout(timeoutHandle);
                    this.finishTimeout = setTimeout(() => this.finishSave(), 1500);
                }
            } else if (this.finishTimeout) {
                clearTimeout(this.finishTimeout);
                this.finishTimeout = null;
            }
        });
    }

    private finishSave(): void {
        this.taskMonitorSub?.unsubscribe();
        this.finishTimeout = null;
        this.savePhase = 'done';
        this.isSaving = false;
    }

    // ─────────────────────────────────────────────────────────────
    // Continue Editing — capture collapse state, ask wrapper for fresh tree.
    // ─────────────────────────────────────────────────────────────

    async continueEditing(): Promise<void> {
        if (!this.config.refreshTree) return;
        const collapsedKeys = new Map<string, boolean>();
        const capture = (n: BuilderTreeNode) => {
            collapsedKeys.set(n.partKey, n.collapsed);
            n.children.forEach(capture);
        };
        this.tree.forEach(capture);

        const refreshed = await this.config.refreshTree(collapsedKeys);
        if (!refreshed) return;
        this.adoptTree(refreshed.initialTree);
        if (refreshed.breadcrumb) this.config.breadcrumb = refreshed.breadcrumb;

        // Restore collapse state by walking the new tree and matching partKeys.
        const restore = (n: BuilderTreeNode) => {
            if (collapsedKeys.has(n.partKey)) {
                n.collapsed = collapsedKeys.get(n.partKey)!;
            }
            n.children.forEach(restore);
        };
        this.tree.forEach(restore);

        // Reset save state.
        this.savePhase = 'idle';
        this.isSaving = false;
        this.saveProgress = 0;
        this.totalChanges = 0;
        this.taskStatuses = [];
        this.tasksCompleted = 0;
        this.totalTasks = 0;
        this.pendingDeleteNode = null;
        this.deletedPartKeys.clear();
        this.suppressAutoSave = true;
        this.rebuildFlatRows();
        this.suppressAutoSave = false;
    }

    // ─────────────────────────────────────────────────────────────
    // Cancel / Close
    // ─────────────────────────────────────────────────────────────

    onCancel(): void {
        this.closeRequested.emit({ saved: this.savedAtLeastOnce });
    }

    onCloseAfterSave(): void {
        this.closeRequested.emit({ saved: this.savedAtLeastOnce });
    }
}
